/* =====================================================================
 * CollabWebSocket — Room 기반 실시간 협업 WebSocket 클라이언트
 *
 * 고도화 기능:
 *   - Jitter 기반 exponential backoff 재연결
 *   - 최대 재연결 횟수 제한 (30회)
 *   - Flapping 방지 (30초 이상 연결 유지 시에만 backoff 리셋)
 *   - navigator.onLine 연동
 *   - Double-probe 연결 검증
 *   - In-band 토큰 갱신
 *   - 3단계 연결 상태
 * ===================================================================== */

import { ensureFreshToken, API_BASE_URL } from './authFetch';
import { connectionHealth } from './connectionHealth';

const WS_BASE = API_BASE_URL.replace(/^http/, 'ws');

type RoomEventHandler = (event: CollabRoomEvent) => void;

export interface CollabRoomEvent {
  type: string;
  room_id: string;
  event: string;
  sender_id?: string;
  data?: unknown;
}

const INITIAL_RETRY_MS = 2_000;
const MAX_RETRY_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;
const PROBE_PONG_TIMEOUT_MS = 5_000;
const MAX_RECONNECT_ATTEMPTS = 30;
const STABLE_CONNECTION_MS = 30_000;
const AUTH_TIMEOUT_MS = 10_000;

class CollabWebSocketClient {
  private ws: WebSocket | null = null;
  private retryMs = INITIAL_RETRY_MS;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private handlers: Map<string, Set<RoomEventHandler>> = new Map();
  private globalHandlers: Set<RoomEventHandler> = new Set();
  private roomRefCounts: Map<string, number> = new Map();
  private _connected = false;
  private _connecting = false;
  private _visibilityBound = false;
  private _onlineBound = false;
  private _lastPingAt = 0;
  private _connectedAt = 0;
  private _reconnectAttempts = 0;
  private _authenticated = false;
  private _authToken = '';
  private _authTimer: ReturnType<typeof setTimeout> | null = null;
  // Tenant-level kill switch — Samsung 등 그룹채팅 미사용 테넌트는
  // ``setEnabled(false)`` 로 connect/join/send 모두 no-op. 기본값은
  // 활성 (B2C / 기존 enterprise 케이스 회귀 방지). main.tsx 가 tenant
  // manifest 의 ``features['collab.enabled']`` 보고 boot 시 호출.
  private _enabled = true;

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    if (!enabled) {
      this.disconnect();
    }
  }

  get enabled(): boolean {
    return this._enabled;
  }

  get isConnected(): boolean {
    return this._connected;
  }

  onEvent(handler: RoomEventHandler): () => void {
    this.globalHandlers.add(handler);
    return () => { this.globalHandlers.delete(handler); };
  }

  onRoomEvent(roomId: string, handler: RoomEventHandler): () => void {
    if (!this.handlers.has(roomId)) {
      this.handlers.set(roomId, new Set());
    }
    this.handlers.get(roomId)!.add(handler);
    return () => {
      this.handlers.get(roomId)?.delete(handler);
      if (this.handlers.get(roomId)?.size === 0) {
        this.handlers.delete(roomId);
      }
    };
  }

  private handleVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;

    if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
      this._reconnectAttempts = 0;
      this.connect();
      return;
    }

    if (this.ws.readyState === WebSocket.OPEN) {
      if (!this._authenticated) return;
      this._lastPingAt = Date.now();
      this.send({ type: 'ping' });
      if (this.pongTimer) clearTimeout(this.pongTimer);
      this.pongTimer = setTimeout(() => {
        console.debug('[CollabWS] visibility probe 타임아웃 → 재연결');
        this.cleanup();
        this.scheduleReconnect();
      }, PROBE_PONG_TIMEOUT_MS);
    }
  };

  private handleOnline = (): void => {
    if (!this._connected && !this._connecting) {
      this._reconnectAttempts = 0;
      this.retryMs = INITIAL_RETRY_MS;
      this.connect();
    }
  };

  private handleOffline = (): void => {
    if (this._authTimer) {
      clearTimeout(this._authTimer);
      this._authTimer = null;
    }
    this.cleanup();
    connectionHealth.markDisconnected('collab');
  };

  async connect(): Promise<void> {
    if (!this._enabled) return;
    if (!this._visibilityBound) {
      document.addEventListener('visibilitychange', this.handleVisibility);
      this._visibilityBound = true;
    }
    if (!this._onlineBound) {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
      this._onlineBound = true;
    }

    if (this.ws || this._connecting) return;
    if (!navigator.onLine) return;
    if (this._reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      connectionHealth.markDisconnected('collab');
      return;
    }

    this._connecting = true;

    const token = await ensureFreshToken();
    if (!token) {
      this._connecting = false;
      this.scheduleReconnect();
      return;
    }
    this._authToken = token;

    const url = `${WS_BASE}/api/v1/collab/ws`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this._connecting = false;
      this.scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      this.ws = ws;
      this._authenticated = false;
      this.send({ type: 'auth', data: { token: this._authToken } });
      this._authTimer = setTimeout(() => {
        this.cleanup();
        this.scheduleReconnect();
      }, AUTH_TIMEOUT_MS);
    };

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        connectionHealth.recordMessageReceived('collab');

        if (data.type === 'auth_ok') {
          if (this._authTimer) {
            clearTimeout(this._authTimer);
            this._authTimer = null;
          }
          this._authenticated = true;
          this._connected = true;
          this._connecting = false;
          this._connectedAt = Date.now();
          this._reconnectAttempts = 0;
          this.retryMs = INITIAL_RETRY_MS;
          connectionHealth.markConnected('collab');

          this.heartbeatTimer = setInterval(() => {
            if (!this._authenticated) return;
            this._lastPingAt = Date.now();
            this.send({ type: 'ping' });
            this.pongTimer = setTimeout(() => {
              if (this.ws?.readyState === WebSocket.OPEN) {
                this.send({ type: 'ping' });
                this.pongTimer = setTimeout(() => {
                  this.cleanup();
                  this.scheduleReconnect();
                }, PROBE_PONG_TIMEOUT_MS);
              } else {
                this.cleanup();
                this.scheduleReconnect();
              }
            }, PONG_TIMEOUT_MS);
          }, HEARTBEAT_INTERVAL_MS);

          for (const roomId of this.roomRefCounts.keys()) {
            this.send({ action: 'join_room', room_id: roomId });
          }
          return;
        }
        if (!this._authenticated) return;

        if (data.type === 'pong') {
          if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null; }
          connectionHealth.recordPong('collab', this._lastPingAt);
          return;
        }
        if (data.type === 'server_ping') return;
        if (data.type === 'server_shutting_down') {
          this.cleanup();
          this.scheduleReconnect();
          return;
        }
        if (data.type === 'token_refreshed') return;

        if (data.type === 'token_expiring_soon') {
          ensureFreshToken().then((newToken) => {
            if (newToken && this.ws?.readyState === WebSocket.OPEN) {
              this.send({ type: 'token_refresh', data: { token: newToken } });
            }
          });
          return;
        }

        if (data.type === 'auth_expired') {
          console.debug('[CollabWS] 서버 토큰 만료 알림 → 토큰 갱신 후 재연결');
          this.cleanup();
          ensureFreshToken().then(() => this.scheduleReconnect());
          return;
        }

        const roomId = data.room_id;

        for (const handler of this.globalHandlers) {
          handler(data);
        }

        if (roomId && this.handlers.has(roomId)) {
          for (const handler of this.handlers.get(roomId)!) {
            handler(data);
          }
        }
      } catch {
        /* 파싱 불가 무시 */
      }
    };

    ws.onclose = () => {
      if (this._authTimer) {
        clearTimeout(this._authTimer);
        this._authTimer = null;
      }
      this.cleanup();
      connectionHealth.markReconnecting('collab');
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      this.cleanup();
      this.scheduleReconnect();
    };
  }

  disconnect(): void {
    this.cleanup();
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this._visibilityBound) {
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this._visibilityBound = false;
    }
    if (this._onlineBound) {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
      this._onlineBound = false;
    }
    this.roomRefCounts.clear();
  }

  joinRoom(roomId: string): void {
    const currentCount = this.roomRefCounts.get(roomId) ?? 0;
    this.roomRefCounts.set(roomId, currentCount + 1);
    if (currentCount > 0) return;
    if (this._connected) {
      this.send({ action: 'join_room', room_id: roomId });
    }
  }

  ensureRoomJoined(roomId: string): void {
    const currentCount = this.roomRefCounts.get(roomId) ?? 0;
    if (currentCount === 0) {
      this.roomRefCounts.set(roomId, 1);
    }
    if (this._connected && currentCount === 0) {
      this.send({ action: 'join_room', room_id: roomId });
    }
  }

  leaveRoom(roomId: string): void {
    const currentCount = this.roomRefCounts.get(roomId) ?? 0;
    if (currentCount <= 1) {
      this.roomRefCounts.delete(roomId);
      if (this._connected) {
        this.send({ action: 'leave_room', room_id: roomId });
      }
      return;
    }

    this.roomRefCounts.set(roomId, currentCount - 1);
    if (this._connected) {
      return;
    }
  }

  sendMessage(roomId: string, content: string, contentType = 'text'): void {
    this.send({
      action: 'send_message',
      room_id: roomId,
      payload: { content, content_type: contentType },
    });
    connectionHealth.recordMessageSent('collab');
  }

  sendTyping(roomId: string, typing: boolean): void {
    this.send({
      action: 'typing',
      room_id: roomId,
      payload: { typing },
    });
  }

  sendReactionUpdate(roomId: string, messageId: string, reactions: unknown[]): void {
    this.send({
      action: 'reaction_update',
      room_id: roomId,
      payload: { message_id: messageId, reactions },
    });
  }

  sendDocUpdate(roomId: string, payload: unknown): void {
    this.send({
      action: 'doc_update',
      room_id: roomId,
      payload,
    });
  }

  private send(data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private cleanup(): void {
    this._authenticated = false;
    this._connected = false;
    this._connecting = false;
    connectionHealth.markDisconnected('collab');
    if (this._authTimer) {
      clearTimeout(this._authTimer);
      this._authTimer = null;
    }
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null; }
    if (this.ws) {
      try { this.ws.close(); } catch { /* noop */ }
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.retryTimer) return;
    if (this._reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      connectionHealth.markDisconnected('collab');
      return;
    }

    // Jitter: retryMs * (0.5 + random * 0.5)
    const jitteredDelay = this.retryMs * (0.5 + Math.random() * 0.5);
    this._reconnectAttempts++;

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, jitteredDelay);

    // Flapping 방지
    if (this._connectedAt > 0 && Date.now() - this._connectedAt >= STABLE_CONNECTION_MS) {
      this.retryMs = INITIAL_RETRY_MS;
    } else {
      this.retryMs = Math.min(this.retryMs * 2, MAX_RETRY_MS);
    }
  }
}

export const collabWS = new CollabWebSocketClient();
